import { useHistory } from 'react-router-dom';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faCircle, faCopy, faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faCloudArrowDown,
    faPaperPlane,
    faPrint,
    faRotateLeft,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DeleteLetterDialog from 'assets/js/pages/engage/letters/DeleteLetterAlertDialog';
import { LetterDownloads } from 'assets/js/pages/engage/letters/LetterDownloads';
import { LetterStatusBadge } from 'assets/js/pages/engage/letters/LetterStatusBadge';
import * as MarkLetterDialog from 'assets/js/pages/engage/letters/MarkLetterDialog';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterActions } from 'assets/js/pages/engage/letters/useLetterActions';
import * as Tooltip from 'components/tooltip/Tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';

const LetterActions = () => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { data: letter } = useCurrentLetter();
    const { duplicateMessageMutation, moveMessageBackToDraftMutation } = useLetterActions();
    const { isPending: isDuplicating, mutate: duplicateMessage } = duplicateMessageMutation;
    const { isPending: isMovingBackToDraft, mutate: moveBackToDraft } =
        moveMessageBackToDraftMutation;
    const isSent = letter.status === 'sent';
    const isAllowedToMarkAsSent = letter.status === 'sending';

    return (
        <Box className={'flex flex-col gap-4 p-6'}>
            <div className={'flex flex-wrap items-start justify-between gap-4 md:flex-nowrap'}>
                <div className={'flex items-center gap-3'}>
                    <p className={'text-xl font-medium text-gray-900'}>{letter.internal_name}</p>
                    {isSent && <LetterStatusBadge status={letter.status} />}
                </div>
                <div className={'flex items-center gap-2'}>
                    <Tooltip.Root>
                        <AlertDialog.Root type="warning">
                            <Tooltip.Trigger>
                                <AlertDialog.Trigger
                                    className={'rounded-md px-2 py-1 hover:bg-gray-300'}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </AlertDialog.Trigger>
                                <AlertDialog.Portal>
                                    <AlertDialog.Overlay />
                                    <AlertDialog.Content>
                                        <AlertDialog.Header>
                                            <AlertDialog.Icon />
                                            <AlertDialog.Close />
                                        </AlertDialog.Header>
                                        <AlertDialog.Body>
                                            <AlertDialog.Title>
                                                Modify letter content?
                                            </AlertDialog.Title>
                                            <AlertDialog.Description>
                                                Modifying a generated letter reverts it to 'draft'
                                                state and removes the activity record from each
                                                recipients contact detail page. After saving and
                                                regenerating, your updated letter will be ready for
                                                download.
                                            </AlertDialog.Description>
                                        </AlertDialog.Body>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                            <Button
                                                variant="solid"
                                                disabled={isMovingBackToDraft}
                                                onClick={() => moveBackToDraft(letter.id)}
                                                className="w-1/2"
                                            >
                                                Continue
                                            </Button>
                                        </AlertDialog.Footer>
                                    </AlertDialog.Content>
                                </AlertDialog.Portal>
                            </Tooltip.Trigger>
                        </AlertDialog.Root>
                        <Tooltip.Portal>
                            <Tooltip.Content className={'!px-3 !py-2'} inverse={true}>
                                <p className={'text-white'}>Modify letter content</p>
                                <Tooltip.Arrow inverse={true} />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                    <Tooltip.Root>
                        <Tooltip.Trigger
                            disabled={isDuplicating}
                            className={'rounded-md px-2 py-1 hover:bg-gray-300'}
                            onClick={() => duplicateMessage(letter.id)}
                        >
                            <FontAwesomeIcon icon={faCopy} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className={'!px-3 !py-2'} inverse={true}>
                                <p className={'text-white'}>Duplicate</p>
                                <Tooltip.Arrow inverse={true} />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            <DeleteLetterDialog.Root
                                messageId={letter.id}
                                onSuccess={() =>
                                    history.replace(`/accounts/${accountId}/engage/letters`)
                                }
                            >
                                <DeleteLetterDialog.Trigger
                                    className={'rounded-md px-2 py-1 hover:bg-gray-300'}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </DeleteLetterDialog.Trigger>
                            </DeleteLetterDialog.Root>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className={'!px-3 !py-2'} inverse={true}>
                                <p className={'text-white'}>Delete</p>
                                <Tooltip.Arrow inverse={true} />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                    {!isSent && (
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild={true}>
                                <div>
                                    <MarkLetterDialog.Root isSending={true} messageId={letter.id}>
                                        <MarkLetterDialog.Trigger asChild={true}>
                                            <Button
                                                disabled={!isAllowedToMarkAsSent}
                                                variant={'outline'}
                                                color={'neutral'}
                                            >
                                                <p>Mark sent</p>
                                                <FontAwesomeIcon icon={faCircle} />
                                            </Button>
                                        </MarkLetterDialog.Trigger>
                                    </MarkLetterDialog.Root>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content className={'!px-3 !py-2'} inverse={true}>
                                    <p className={'text-white'}>
                                        {isAllowedToMarkAsSent
                                            ? `Marking a letter as 'sent' is for internal tracking purpose and doesn't modify a letter's content or notify donors. This action is reversible at anytime.`
                                            : 'Downloads not available yet'}
                                    </p>
                                    <Tooltip.Arrow inverse={true} />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    )}
                    {isSent && (
                        <Tooltip.Root>
                            <Tooltip.Trigger className={'rounded-md px-2 py-1 hover:bg-gray-300'}>
                                <div>
                                    <MarkLetterDialog.Root isSending={false} messageId={letter.id}>
                                        <MarkLetterDialog.Trigger>
                                            <FontAwesomeIcon icon={faRotateLeft} />
                                        </MarkLetterDialog.Trigger>
                                    </MarkLetterDialog.Root>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content className={'!px-3 !py-2'} inverse={true}>
                                    <p className={'text-white'}>Mark unsent</p>
                                    <Tooltip.Arrow inverse={true} />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    )}
                </div>
            </div>
            {!isSent && (
                <div
                    className={
                        'bg-gray-25 flex flex-col gap-4 rounded-lg border border-gray-200 p-6'
                    }
                >
                    <p className={'text-brand-violet-primary text-lg font-medium'}>What's next?</p>
                    <div className={'flex flex-col gap-8 md:flex-row'}>
                        <div className={'flex flex-1 flex-col items-start'}>
                            <FontAwesomeIcon className={'text-gray-400'} icon={faCloudArrowDown} />
                            <p className={'text-md mt-2 font-medium text-gray-900'}>
                                Download letters
                            </p>
                            <p className={'text-sm font-normal leading-snug text-gray-800'}>
                                When your letters have finished generating, download them to your
                                computer.
                            </p>
                        </div>
                        <div className={'flex flex-1 flex-col items-start'}>
                            <FontAwesomeIcon className={'text-gray-400'} icon={faPrint} />
                            <p className={'text-md mt-2 font-medium text-gray-900'}>Print & mail</p>
                            <p className={'text-sm font-normal leading-snug text-gray-800'}>
                                Print your letters, insert them into envelopes, and mail them to
                                your supporters.
                            </p>
                        </div>
                        <div className={'flex flex-1 flex-col items-start'}>
                            <FontAwesomeIcon className={'text-gray-400'} icon={faPaperPlane} />
                            <p className={'text-md mt-2 font-medium text-gray-900'}>Mark as sent</p>
                            <p className={'text-sm font-normal leading-snug text-gray-800'}>
                                Once the letters have been mailed, mark your letter as sent above
                                for record-keeping.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <Separator className={'-mx-6 !w-auto'} />
            <LetterDownloads />
        </Box>
    );
};

export { LetterActions };
